import { formatLeadTimeWeeks } from '@lib/calculations/lead-time/calculate-weeks';
import { LeadTimeFormat } from '@snap-types/models/enums';
import { Nullish } from '@snap-types/types/nullish';

type LeadTimeDisplayParams = {
  quotePrices: { leadTimeWeeks: Nullish<number> }[];
  leadTimeNotes: Nullish<string>;
  leadTimeFormat: Nullish<LeadTimeFormat>;
  /** The line's lead time varies by quantity break. */
  isQuantityAwareLeadTime: boolean;
  /** The shortest lead time across the line's prices, in whole weeks. */
  leadTimeWeeks: Nullish<number>;
};

type LeadTimeDisplay = {
  /** Text for the line-level "Lead Time" row, or null to omit the row. */
  lineText: string | null;
  /** Whether the price table needs its own per-price lead time column. */
  showPriceColumn: boolean;
};

/**
 * Where a line's lead time gets quoted depends on whether one figure can speak
 * for the whole line.
 *
 * A quantity-aware line has a lead time per quantity break, so each one is
 * quoted next to its price even when the breaks happen to agree — the customer
 * needs to see that ordering more can cost them time.
 *
 * Any other line whose breaks disagree gets the column too. Lead time is stored
 * per price and a rep can override a single break by hand under any strategy, so
 * disagreeing breaks are not exclusive to the quantity-aware config. Collapsing
 * them to the shortest would quote the best case to someone ordering a larger
 * break.
 *
 * A line whose breaks all agree keeps its one figure in the line-level row.
 *
 * Sites configured for TEXT never show numbers at all, so they keep their notes
 * and never get the column.
 */
export function getLeadTimeDisplay({
  quotePrices,
  leadTimeNotes,
  leadTimeFormat,
  isQuantityAwareLeadTime,
  leadTimeWeeks,
}: LeadTimeDisplayParams): LeadTimeDisplay {
  const format = leadTimeFormat ?? LeadTimeFormat.NUMBER;

  if (format === LeadTimeFormat.TEXT) {
    return { lineText: leadTimeNotes || null, showPriceColumn: false };
  }

  const notes = format === LeadTimeFormat.BOTH ? leadTimeNotes || null : null;

  // A break with no lead time counts as disagreeing with one that has a value.
  // The column shows the gap as "-", where the line-level row would read as a
  // promise covering the break we know nothing about.
  const leadTimesVary =
    new Set(quotePrices.map((price) => price.leadTimeWeeks ?? null)).size > 1;

  if (isQuantityAwareLeadTime || leadTimesVary) {
    // The column carries the values, so the line-level row is only ever notes.
    return {
      lineText: notes,
      showPriceColumn: quotePrices.some((price) => price.leadTimeWeeks != null),
    };
  }

  if (leadTimeWeeks == null) {
    return { lineText: notes, showPriceColumn: false };
  }

  const weeks = formatLeadTimeWeeks(leadTimeWeeks);

  return {
    lineText: notes ? `${weeks} (${notes})` : weeks,
    showPriceColumn: false,
  };
}
